import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, CheckCircle, XCircle, Clock, Eye, RefreshCw, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { BuyTotalBackfillReport } from '../../components/admin/BuyTotalBackfillReport';

const AdminPANVerificationPage = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total_count: 0,
    total_pages: 1
  });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [panPhoto, setPanPhoto] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await adminAPI.get(`/pan/list?${params.toString()}`);
      setSubmissions(response.data.submissions || []);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (error) {
      console.error('Failed to fetch PAN submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter, searchQuery]);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.get('/pan/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch PAN stats:', error);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    fetchStats();
  }, [fetchSubmissions]);

  const handleViewPhoto = async (user) => {
    setSelectedUser(user);
    setPhotoLoading(true);
    setPanPhoto(null);
    
    try {
      const response = await adminAPI.get(`/pan/${user.user_id}/photo`);
      setPanPhoto(response.data.pan_photo);
    } catch (error) {
      console.error('Failed to fetch PAN photo:', error);
      alert('Failed to load PAN photo');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    if (!window.confirm('Are you sure you want to approve this PAN?')) return;
    
    setActionLoading(true);
    try {
      await adminAPI.post(`/pan/${userId}/approve`, {});
      await fetchSubmissions();
      await fetchStats();
      setSelectedUser(null);
      setPanPhoto(null);
    } catch (error) {
      console.error('Failed to approve PAN:', error);
      alert(error.response?.data?.detail || 'Failed to approve PAN');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(true);
    try {
      await adminAPI.post(`/pan/${selectedUser.user_id}/reject`, {
        reason: rejectReason.trim()
      });
      await fetchSubmissions();
      await fetchStats();
      setSelectedUser(null);
      setPanPhoto(null);
      setShowRejectModal(false);
      setRejectReason('');
    } catch (error) {
      console.error('Failed to reject PAN:', error);
      alert(error.response?.data?.detail || 'Failed to reject PAN');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
            <CheckCircle className="w-3 h-3" /> Verified
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
            <XCircle className="w-3 h-3" /> Rejected
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
            <Clock className="w-3 h-3" /> Pending
          </span>
        );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-yellow-500 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold">PAN Verification</h1>
          <p className="text-white/80 mt-1">Review and verify user PAN submissions</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="text-2xl font-bold text-gray-900">{stats.total_submissions}</div>
              <div className="text-sm text-gray-500">Total Submissions</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-yellow-200 bg-yellow-50">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
              <div className="text-sm text-yellow-600">Pending Review</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-green-200 bg-green-50">
              <div className="text-2xl font-bold text-green-600">{stats.verified}</div>
              <div className="text-sm text-green-600">Verified</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-red-200 bg-red-50">
              <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
              <div className="text-sm text-red-600">Rejected</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-orange-200 bg-orange-50">
              <div className="text-2xl font-bold text-orange-600">{stats.users_needing_pan}</div>
              <div className="text-sm text-orange-600">Need PAN (₹2L+)</div>
            </div>
          </div>
        )}

        <BuyTotalBackfillReport />

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchSubmissions()}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            {/* Refresh */}
            <button
              onClick={() => { fetchSubmissions(); fetchStats(); }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No PAN submissions found
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">User</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">PAN</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Total Bought</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Submitted</th>
                      <th className="text-center px-6 py-4 text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {submissions.map((sub) => (
                      <tr key={sub.user_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{sub.user_name}</div>
                          <div className="text-sm text-gray-500">{sub.user_phone}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-mono text-gray-700">XXXXXX{sub.pan_last_4}</span>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(sub.pan_status)}
                          {sub.pan_status === 'rejected' && sub.pan_rejection_reason && (
                            <div className="text-xs text-red-600 mt-1 max-w-[200px] truncate" title={sub.pan_rejection_reason}>
                              Reason: {sub.pan_rejection_reason}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={sub.total_successful_buy_inr >= 200000 ? 'text-orange-600 font-medium' : 'text-gray-700'}>
                            {formatCurrency(sub.total_successful_buy_inr)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(sub.pan_submitted_at)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleViewPhoto(sub)}
                              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                              title="View PAN Photo"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {sub.pan_status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApprove(sub.user_id)}
                                  disabled={actionLoading}
                                  className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                                  title="Approve"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => { setSelectedUser(sub); setShowRejectModal(true); }}
                                  disabled={actionLoading}
                                  className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                                  title="Reject"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <div className="text-sm text-gray-500">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total_count)} of {pagination.total_count}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page <= 1}
                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-600">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.total_pages}
                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Photo View Modal */}
      {selectedUser && !showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">PAN Verification</h2>
              <p className="text-gray-500 mt-1">{selectedUser.user_name} ({selectedUser.user_phone})</p>
            </div>
            
            <div className="p-6">
              {/* User Details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <div className="text-sm text-gray-500">PAN Number</div>
                  <div className="font-mono text-lg">XXXXXX{selectedUser.pan_last_4}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <div>{getStatusBadge(selectedUser.pan_status)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Total Purchased</div>
                  <div className="font-medium">{formatCurrency(selectedUser.total_successful_buy_inr)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Submitted On</div>
                  <div>{formatDate(selectedUser.pan_submitted_at)}</div>
                </div>
              </div>
              
              {/* PAN Photo */}
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                {photoLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                  </div>
                ) : panPhoto ? (
                  <img
                    src={panPhoto.startsWith('data:') ? panPhoto : `data:image/jpeg;base64,${panPhoto}`}
                    alt="PAN Card"
                    className="w-full h-auto"
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-400">
                    Failed to load photo
                  </div>
                )}
              </div>
              
              {/* Verification Instructions */}
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-semibold text-amber-800 mb-2">Verification Checklist:</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>✓ PAN card photo is clear and readable</li>
                  <li>✓ Name on PAN matches user&apos;s registered name</li>
                  <li>✓ PAN number is visible and matches last 4 digits</li>
                  <li>✓ Photo is of original PAN card (not photocopy/screenshot)</li>
                </ul>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => { setSelectedUser(null); setPanPhoto(null); }}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              {selectedUser.pan_status === 'pending' && (
                <>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-colors disabled:opacity-50"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApprove(selectedUser.user_id)}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {actionLoading ? 'Processing...' : 'Approve'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">Reject PAN</h2>
              <p className="text-gray-500 mt-1">Provide a reason for rejection</p>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <div className="text-sm text-gray-500">User</div>
                <div className="font-medium">{selectedUser.user_name} ({selectedUser.user_phone})</div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g., Photo is blurry, Name doesn't match, etc."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                />
              </div>
              
              {/* Quick Reasons */}
              <div className="mt-3 flex flex-wrap gap-2">
                {['Photo is blurry', 'Name mismatch', 'Invalid PAN format', 'Document not readable'].map(reason => (
                  <button
                    key={reason}
                    onClick={() => setRejectReason(reason)}
                    className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading || !rejectReason.trim()}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Rejecting...' : 'Reject PAN'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPANVerificationPage;
